// src/pages/admin/ManageScoringSessionPage.js
// Updated: 11/09/2025 08:56 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// --- 1. Import 'functions' and 'httpsCallable' ---
import { db, storage, functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
// --- 2. Remove 'updateDoc' ---
import { doc, collection, query, onSnapshot, orderBy, deleteDoc, getDocs, where, getDoc } from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Edit, Plus, Users, Play, Settings, Trash2, Lock, FileText, Upload, CheckCircle, Clock, ShieldCheck } from 'lucide-react';

import AddPackageModal from '../../components/AddPackageModal';
import AssignReviewersModal from '../../components/AssignReviewersModal';
import AddGuidanceModal from '../../components/AddGuidanceModal';
import AssignSponsorsModal from '../../components/AssignSponsorsModal';

// --- 3. Define new callable functions ---
const adminChangeSessionStatus = httpsCallable(functions, 'adminChangeSessionStatus');

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

export default function ManageScoringSessionPage() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [packages, setPackages] = useState([]);
  const [guidanceDocs, setGuidanceDocs] = useState([]);
  const [reviewersDetails, setReviewersDetails] = useState([]);

  const [sponsorsDetails, setSponsorsDetails] = useState([]);
  const [isLoadingSponsors, setIsLoadingSponsors] = useState(false);
  const [isAssignSponsorsModalOpen, setIsAssignSponsorsModalOpen] = useState(false);

  const [scoreCounts, setScoreCounts] = useState({});
  const [isLoadingScores, setIsLoadingScores] = useState(true);

  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingGuidance, setIsLoadingGuidance] = useState(true);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userData, currentUser } = useAuth();

  const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);
  const [isAssignReviewersModalOpen, setIsAssignReviewersModalOpen] = useState(false);
  const [isAddGuidanceModalOpen, setIsAddGuidanceModalOpen] = useState(false);

  // --- No changes to useEffect hooks ---
  useEffect(() => {
    setIsLoadingSession(true);
    setError('');
    if (!sessionId) {
        setError('Session ID is missing.');
        setIsLoadingSession(false);
        return;
    }
    const sessionRef = doc(db, 'scoringSessions', sessionId);
    const unsubscribe = onSnapshot(sessionRef, (docSnap) => {
      if (docSnap.exists()) {
        const sessionData = { id: docSnap.id, ...docSnap.data() };
        if (userData?.orgId && userData.orgId !== sessionData.orgId) {
            setError('Access Denied: Session belongs to another organization.');
            setSession(null);
        } else {
            setSession(sessionData);
            setError(prev => prev === 'Scoring session not found.' || prev.includes('Access Denied') ? '' : prev);
        }
      } else {
        setError('Scoring session not found.');
        setSession(null);
      }
      setIsLoadingSession(false);
    }, (err) => {
        console.error("Error fetching session:", err);
        setError("Failed to load session details.");
        setIsLoadingSession(false);
    });
    return () => unsubscribe();
  }, [sessionId, userData?.orgId]);

  useEffect(() => {
     if (!sessionId) return;
     setIsLoadingPackages(true);
     const packagesQuery = query( collection(db, 'scoringSessions', sessionId, 'packages'), orderBy('addedAt', 'asc') );
     const unsubscribePackages = onSnapshot(packagesQuery, (querySnapshot) => {
         const pkgs = []; querySnapshot.forEach((doc) => pkgs.push({ id: doc.id, ...doc.data() }) ); setPackages(pkgs); setIsLoadingPackages(false);
     }, (err) => { console.error("Error fetching packages:", err); setError(prev => (prev ? prev + " " : "") + "Failed to load packages."); setIsLoadingPackages(false); });
     return () => unsubscribePackages();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    setIsLoadingGuidance(true);
    const guidanceQuery = query( collection(db, 'scoringSessions', sessionId, 'guidanceDocs'), orderBy('uploadedAt', 'desc') );
    const unsubscribeGuidance = onSnapshot(guidanceQuery, (querySnapshot) => {
        const docs = [];
        querySnapshot.forEach((doc) => {
            docs.push({ id: doc.id, ...doc.data() });
        });
        setGuidanceDocs(docs);
        setIsLoadingGuidance(false);
    }, (err) => {
        console.error("Error fetching guidance documents:", err);
        setError(prev => (prev ? prev + " " : "") + "Failed to load guidance documents.");
        setIsLoadingGuidance(false);
    });
    return () => unsubscribeGuidance();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    setIsLoadingScores(true);
    const scoresQuery = query(collection(db, 'scoringSessions', sessionId, 'scores'));
    const unsubscribeScores = onSnapshot(scoresQuery, (querySnapshot) => {
      const counts = {};
      querySnapshot.forEach((doc) => {
        const reviewerId = doc.data().reviewerId;
        if (reviewerId) {
          counts[reviewerId] = (counts[reviewerId] || 0) + 1;
        }
      });
      setScoreCounts(counts);
      setIsLoadingScores(false);
    }, (err) => {
      console.error("Error fetching scores:", err);
      setError(prev => (prev ? prev + " " : "") + "Failed to load scores.");
      setIsLoadingScores(false);
    });
    return () => unsubscribeScores();
  }, [sessionId]);

   useEffect(() => {
       if (!userData?.orgId || !session?.reviewers || session.reviewers.length === 0) {
           setReviewersDetails([]); setIsLoadingReviewers(false); return;
       }
       setIsLoadingReviewers(true);
       const reviewerIds = session.reviewers;
       const q = query(collection(db, 'users'), where('__name__', 'in', reviewerIds), where('orgId', '==', userData.orgId));
       getDocs(q).then((querySnapshot) => {
           const details = []; querySnapshot.forEach((doc) => { details.push({ id: doc.id, ...doc.data() }); }); setReviewersDetails(details);
        })
           .catch(err => { console.error("Error fetching reviewer details:", err); setError(prev => (prev ? prev + " " : "") + "Failed to load reviewer details."); })
           .finally(() => setIsLoadingReviewers(false));
   }, [session?.reviewers, userData?.orgId]);

  useEffect(() => {
    if (!userData?.orgId || !session?.sessionSponsors || session.sessionSponsors.length === 0) {
        setSponsorsDetails([]); setIsLoadingSponsors(false); return;
    }
    setIsLoadingSponsors(true);
    const sponsorIds = session.sessionSponsors;
    const q = query(collection(db, 'users'), where('__name__', 'in', sponsorIds), where('orgId', '==', userData.orgId));
    getDocs(q).then((querySnapshot) => {
        const details = []; querySnapshot.forEach((doc) => { details.push({ id: doc.id, ...doc.data() }); }); setSponsorsDetails(details);
     })
        .catch(err => { console.error("Error fetching sponsor details:", err); setError(prev => (prev ? prev + " " : "") + "Failed to load sponsor details."); })
        .finally(() => setIsLoadingSponsors(false));
  }, [session?.sessionSponsors, userData?.orgId]);
  // --- End of useEffect hooks ---


  // --- 4. UPDATE Action Handlers ---
  const handleOpenSession = async () => {
    if (!session || session.status !== 'setup' || isSubmitting || !(packages.length > 0 && (session.reviewers || []).length > 0)) return;
    setIsSubmitting(true);
    try {
      // Use the callable function
      await adminChangeSessionStatus({
        sessionId: sessionId,
        newStatus: 'open',
        orgId: session.orgId
      });
      // UI will update from onSnapshot listener
    } catch (err) {
      console.error("Error opening session:", err);
      setError(`Failed to open session: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSession = async () => {
    if (!session || session.status !== 'open' || isSubmitting) return;
    if (!window.confirm("Are you sure you want to close this scoring session? Scores can no longer be submitted.")) return;
    setIsSubmitting(true);
    try {
      // Use the callable function
      await adminChangeSessionStatus({
        sessionId: sessionId,
        newStatus: 'closed',
        orgId: session.orgId
      });
      // UI will update from onSnapshot listener
    } catch (err) {
      console.error("Error closing session:", err);
      setError(`Failed to close session: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- No changes to package/guidance deletion ---
  const handleDeletePackage = async (packageId) => {
    if (!sessionId || !packageId || session?.status !== 'setup') return;
    if (!window.confirm("Are you sure you want to delete this package?")) return;
    setIsSubmitting(true); const packageRef = doc(db, 'scoringSessions', sessionId, 'packages', packageId);
    try { await deleteDoc(packageRef); }
    catch (err) { console.error("Error deleting package:", err); setError("Failed to delete package."); }
    finally { setIsSubmitting(false); }
  };

  const handleRemoveGuidance = async (docRefId, title) => {
    if (!sessionId || !docRefId || session?.status !== 'setup') return;
    if (!window.confirm(`Are you sure you want to remove the guidance document "${title}" from this session? This will also delete the document from the library.`)) return;
    setIsSubmitting(true);
    setError('');
    try {
      const linkDocRef = doc(db, 'scoringSessions', sessionId, 'guidanceDocs', docRefId);
      const linkDocSnap = await getDoc(linkDocRef);
      if (!linkDocSnap.exists()) throw new Error("Guidance link not found.");
      const originalDocId = linkDocSnap.data().documentId;
      const originalDocRef = doc(db, 'documents', originalDocId);
      const originalDocSnap = await getDoc(originalDocRef);
      if (!originalDocSnap.exists()) throw new Error("Original document not found.");
      const storagePath = originalDocSnap.data().storagePath;
      if (storagePath) {
        const fileRef = storageRef(storage, storagePath);
        await deleteObject(fileRef);
      }
      await deleteDoc(originalDocRef);
      await deleteDoc(linkDocRef);
    } catch (err) {
      console.error("Error removing guidance document:", err);
      setError(`Failed to remove guidance document: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- End of action handlers ---


  // --- No changes to helper components or render logic below ---
  const getOpenButtonTitle = () => {
    const hasPackages = packages.length > 0;
    const hasReviewers = (session?.reviewers || []).length > 0;
    if (!hasPackages) return "Add packages first";
    if (!hasReviewers) return "Assign reviewers first";
    return "Open session for scoring";
  };

  const ReviewerStatus = ({ reviewerId, totalPackages }) => {
    if (isLoadingScores || isLoadingPackages) {
      return <span className="text-xs text-gray-400 italic">Loading status...</span>;
    }
    const scoreCount = scoreCounts[reviewerId] || 0;
    const isComplete = totalPackages > 0 && scoreCount === totalPackages;
    const isInProgress = scoreCount > 0 && scoreCount < totalPackages;
    let text = `Not Started (${scoreCount}/${totalPackages})`;
    let color = 'bg-gray-100 text-gray-700';
    let Icon = Clock;
    if (isComplete) {
      text = `Completed (${scoreCount}/${totalPackages})`;
      color = 'bg-green-100 text-green-700';
      Icon = CheckCircle;
    } else if (isInProgress) {
      text = `In Progress (${scoreCount}/${totalPackages})`;
      color = 'bg-blue-100 text-blue-700';
      Icon = Clock;
    } else if (totalPackages === 0 && scoreCount === 0) {
      text = 'No packages';
      color = 'bg-yellow-100 text-yellow-700';
      Icon = Settings;
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {text}
      </span>
    );
  };

  const isGlobalAdmin = userData?.role === 'admin';
  const isSessionSponsor = !isLoadingSession && session?.sessionSponsors?.includes(currentUser?.uid);
  const canManage = isGlobalAdmin || isSessionSponsor;

  if (!isLoadingSession && !canManage) {
    return (<div> <Link to="/scoring" className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"> <ArrowLeft className="mr-2 h-4 w-4" /> Back to Scoring Sessions </Link> <p className="mt-4 text-red-600">Access Denied. You must be an admin or session sponsor to manage this page.</p> </div>);
  }
  if (isLoadingSession) return <LoadingSpinner />;
  if (error && !session) return <p className="text-red-600">{error}</p>;
  if (!session) return <p>Scoring session not found or access denied.</p>;

  return (
    <div>
      <Link to="/scoring" className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Scoring Sessions
      </Link>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div> <h1 className="text-3xl font-bold text-gray-900">{session.name}</h1> <p className="mt-1 text-sm text-gray-500">{session.description || 'No description'}</p> </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {session.status === 'setup' && <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800"><Settings className="mr-1 h-4 w-4" />Setup</span>}
            {session.status === 'open' && <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"><Play className="mr-1 h-4 w-4" />Open</span>}
            {session.status === 'closed' && <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800"><Lock className="mr-1 h-4 w-4" />Closed</span>}
            {session.status === 'setup' && canManage && (
              <button
                onClick={handleOpenSession}
                disabled={isSubmitting || !(packages.length > 0 && (session.reviewers || []).length > 0)}
                className="flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                title={getOpenButtonTitle()}
              >
                <Play className="mr-2 h-5 w-5" /> Open Session
              </button>
            )}
            {session.status === 'open' && canManage && (<button onClick={handleCloseSession} disabled={isSubmitting} className="flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50" title="Close session to prevent further scoring" > <Lock className="mr-2 h-5 w-5" /> Close Session </button>)}
          </div>
        </div>
        {error && session && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-semibold text-gray-800">Session Sponsors ({(session?.sessionSponsors || []).length})</h2>
              {isGlobalAdmin && (
                <button
                  onClick={() => setIsAssignSponsorsModalOpen(true)}
                  disabled={isSubmitting}
                  className="flex items-center rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:opacity-50"
                >
                  <ShieldCheck className="mr-1 h-4 w-4" /> Assign Sponsors
                </button>
              )}
            </div>
            <div className="mt-4">
              {isLoadingSponsors ? <LoadingSpinner /> :
                sponsorsDetails.length === 0 ? (
                  <p className="text-gray-500">No session sponsors assigned.</p>
                ) : (
                  <ul className="space-y-2">
                    {sponsorsDetails.map(sp => (
                      <li key={sp.id} className="text-sm">
                        <span className="font-medium">{sp.displayName}</span>
                        <span className="text-gray-500"> ({sp.email})</span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-semibold text-gray-800">Reviewers ({(session?.reviewers || []).length})</h2>
              {session.status !== 'closed' && canManage && (
                <button
                  onClick={() => setIsAssignReviewersModalOpen(true)}
                  disabled={isSubmitting}
                  className="flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Users className="mr-1 h-4 w-4" /> Assign/Modify
                </button>
              )}
            </div>
            <div className="mt-4">
              {isLoadingReviewers || isLoadingScores || isLoadingPackages ? <LoadingSpinner /> :
                reviewersDetails.length === 0 ? (
                  <p className="text-gray-500">
                    {session.status === 'closed' ? 'No reviewers were assigned.' : 'Click "Assign/Modify" to select reviewers.'}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {reviewersDetails.map(rev => {
                      const totalPackages = packages.length;
                      return (
                        <li key={rev.id} className="text-sm">
                          <div>
                            <span className="font-medium">{rev.displayName}</span>
                            <span className="text-gray-500"> ({rev.email})</span>
                          </div>
                          <div className="mt-1">
                            <ReviewerStatus reviewerId={rev.id} totalPackages={totalPackages} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
            </div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">Scoring Rubric</h2>
              {session.status === 'setup' && canManage && (
                <Link
                  to={`/admin/scoring/new?edit=${sessionId}`}
                  className="text-blue-600 hover:text-blue-800"
                  title="Edit Rubric"
                >
                  <Edit className="h-4 w-4" />
                </Link>
              )}
              {session.status !== 'setup' && (<button disabled className="cursor-not-allowed text-blue-600 opacity-50" title="Rubric cannot be edited after session setup is complete"> <Edit className="h-4 w-4" /> </button>)}
            </div>
            <div className="mt-4 space-y-3">
              {session.categories?.map((cat, index) => (
                <div key={index}>
                  <h3 className="font-semibold">{cat.name}</h3>
                  {session.useSimplifiedRubric ? (
                    <p className="ml-4 text-sm text-gray-600">Max Points: {cat.maxPoints} {cat.simplifiedCriteria && `(Details: ${cat.simplifiedCriteria.substring(0, 50)}...)`}</p>
                  ) : (
                    <ul className="ml-4 list-disc text-sm text-gray-600">
                      {cat.criteria?.map((crit, cIndex) => (<li key={cIndex}> {crit.name} (Max: {crit.maxPoints}, Weight: {crit.weight}) </li>))}
                    </ul>
                  )}
                </div>
              ))}
              {((!session.categories) || (session.categories.length === 0)) && <p className="text-sm text-gray-500">No rubric defined.</p>}
            </div>
          </div>
        </div>
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">Guidance Docs ({guidanceDocs.length})</h2>
              {session.status === 'setup' && canManage && (
                <button onClick={() => setIsAddGuidanceModalOpen(true)} disabled={isSubmitting} className="flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50" >
                  <Upload className="mr-1 h-4 w-4" /> Upload Guidance
                </button>
              )}
            </div>
            <div className="mt-4">
              {isLoadingGuidance ? <LoadingSpinner /> : guidanceDocs.length === 0 ? (
                <p className="text-gray-500">
                  {session.status === 'setup' ? 'No guidance uploaded. Use the button above.' : 'No guidance documents provided.'}
                </p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {guidanceDocs.map(doc => (
                    <li key={doc.id} className="flex items-center justify-between py-3">
                      <Link to={`/documents/${doc.documentId}`} target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-blue-600">
                        <FileText className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{doc.title}</span>
                      </Link>
                      {session.status === 'setup' && canManage && (
                        <button
                          onClick={() => handleRemoveGuidance(doc.id, doc.title)}
                          disabled={isSubmitting}
                          className="ml-4 flex-shrink-0 rounded p-1 text-red-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                          title="Delete this guidance document"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between"> <h2 className="text-xl font-semibold text-gray-800">Packages to Score ({packages.length})</h2>
              {session.status === 'setup' && canManage && (<button onClick={() => setIsAddPackageModalOpen(true)} disabled={isSubmitting} className="flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50" > <Plus className="mr-1 h-4 w-4" /> Add Package </button>)} </div>
            <div className="mt-4"> {isLoadingPackages ? <LoadingSpinner /> : packages.length === 0 ? (<p className="text-gray-500">{session.status === 'setup' ? 'Click "Add Package" to add items.' : 'No packages were added.'}</p>) :
              (<ul className="divide-y divide-gray-200"> {packages.map(pkg => (<li key={pkg.id} className="flex items-center justify-between py-3"> <div> <span className="font-medium text-gray-900">{pkg.name}</span> {pkg.description && <p className="text-sm text-gray-500">{pkg.description}</p>} </div>
                {session.status === 'setup' && canManage && (<button onClick={() => handleDeletePackage(pkg.id)} disabled={isSubmitting} className="ml-4 flex-shrink-0 rounded p-1 text-red-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50" title="Delete Package" > <Trash2 className="h-4 w-4" /> </button>)} </li>))} </ul>)}
            </div>
          </div>
        </div>
      </div>
      <AddPackageModal isOpen={isAddPackageModalOpen} onClose={() => setIsAddPackageModalOpen(false)} session={session} />
      <AssignReviewersModal isOpen={isAssignReviewersModalOpen} onClose={() => setIsAssignReviewersModalOpen(false)} session={session} />
      <AddGuidanceModal
        isOpen={isAddGuidanceModalOpen}
        onClose={() => setIsAddGuidanceModalOpen(false)}
        session={session}
      />
      <AssignSponsorsModal
        isOpen={isAssignSponsorsModalOpen}
        onClose={() => setIsAssignSponsorsModalOpen(false)}
        session={session}
      />
    </div>
  );
}